import React from 'react';
import { OfferOptionsRadioButtonsProps } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferOptions/OfferOptions.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesOfferOptionGroupSeenEventActivitiesFieldsV1,
    ActivitiesOfferOptionNotificationSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesOfferOptionGroupSeenEventActivitiesManifestV1,
    ActivitiesOfferOptionNotificationSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { Box, Chip, Notification, RadioButton, Typography } from '@drone-js/core';
import { StringFormatter } from '@agoda/react-handyman';
import { getSpecification } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferOptions/OfferOptionsUtils';
import { usePackageOptionsModularContext } from 'component/page/detail/PackageOptions/PackageOptionsModularContext';

const GenericSpecifications: React.FC<OfferOptionsRadioButtonsProps> = (props) => {
    const { cms, groupIndex, mappedSpecKey, mappedSpecValues, selectedOfferId, isShowOptionRadioNotification, activityId, isSelected, onSelected, isRadioAvailable } = props;
    const { isChipOfferOption } = usePackageOptionsModularContext();

    React.useEffect(() => {
        trackElementEvent<ActivitiesOfferOptionGroupSeenEventActivitiesFieldsV1>(
            ActivitiesOfferOptionGroupSeenEventActivitiesManifestV1.ids,
            { activities_offer_group: mappedSpecKey || '' },
            { offer_id: selectedOfferId, action_element_index: groupIndex + 1, activity_id: activityId },
        );
    }, [groupIndex, mappedSpecKey, selectedOfferId, activityId]);

    React.useEffect(() => {
        if (isShowOptionRadioNotification) {
            trackElementEvent<ActivitiesOfferOptionNotificationSeenEventActivitiesFieldsV1>(
                ActivitiesOfferOptionNotificationSeenEventActivitiesManifestV1.ids,
                { activities_offer_group: mappedSpecKey || '' },
                { offer_id: selectedOfferId, activity_id: activityId },
            );
        }
    }, [isShowOptionRadioNotification, mappedSpecKey, selectedOfferId, activityId]);

    return (
        <Box
            key={mappedSpecKey}
            pb="12"
            data-element-name="activities-offer-option-group"
            data-testid="activities-offer-option-group"
            data-element-index={groupIndex + 1}
            as="fieldset"
            borderWidth="0"
            p="0"
        >
            <Typography variant="body-s-strong" tag="legend" p="0">
                {mappedSpecKey}
            </Typography>
            <Typography variant="caption-default" pb="4">
                {StringFormatter.formatToJsx(cms[305464], 1)}
            </Typography>
            {isShowOptionRadioNotification && (
                <Notification
                    data-element-name="activities-offer-option-notification"
                    data-testid="activities-offer-option-notification"
                    intent="warning"
                    title={cms[305873]}
                    asset={{ id: 'fill.symbol.circle-info' }}
                    radius="m"
                />
            )}
            {isChipOfferOption ? (
                <Box display="flex" flexWrap="wrap" gap="8" pt="8">
                    {mappedSpecValues.map((specification, radioIndex) => {
                        const radioSpecification = getSpecification(mappedSpecKey, specification);
                        return (
                            <Chip
                                key={specification}
                                data-element-name="activities-offer-option-radio-button"
                                data-testid="activities-offer-option-chip"
                                data-element-index={radioIndex + 1}
                                label={specification || ''}
                                selection={isSelected(radioSpecification) ? 'selected' : 'unselected'}
                                onClick={() => onSelected(radioSpecification, radioIndex + 1, true)}
                                state={isRadioAvailable(radioSpecification) ? 'enabled' : 'disabled'}
                            />
                        );
                    })}
                </Box>
            ) : (
                mappedSpecValues.map((specification, radioIndex) => {
                    const radioSpecification = getSpecification(mappedSpecKey, specification);
                    return (
                        <Box
                            key={specification}
                            py="8"
                            data-element-name="activities-offer-option-radio-button"
                            data-testid="activities-offer-option-radio-button"
                            data-element-index={radioIndex + 1}
                        >
                            <RadioButton
                                value={specification}
                                name={mappedSpecKey}
                                selection={isSelected(radioSpecification) ? 'selected' : 'unselected'}
                                onChange={() => onSelected(radioSpecification, radioIndex + 1, true)}
                                state={isRadioAvailable(radioSpecification) ? 'enabled' : 'disabled'}
                            >
                                <Typography variant="body-s">{specification}</Typography>
                            </RadioButton>
                        </Box>
                    );
                })
            )}
        </Box>
    );
};
GenericSpecifications.displayName = 'OfferOptionsRadioButtons';

export default GenericSpecifications;
